/* eslint-disable react/destructuring-assignment */
import type { FC } from 'react';
import { useLayoutEffect, useRef } from 'react';
import { classNames } from '../../utils/tailwind/classNames';
import { isServer } from '../../utils/utils';
import { Content } from '../content-asset/Content';
import styles from './HtmlSnippet.module.scss';

export type HtmlSnippetProps = {
    id: string;
    html: string;
};

// https://macarthur.me/posts/script-tags-in-react/
/**
 * This component is used to render HTML snippets that contain script tags.
 * Unlike dangerouslySetInnerHTML which only renders the HTML but does not execute scripts,
 * this component properly executes any script tags in the HTML by using createContextualFragment.
 * dangerouslySetInnerHTML is not sufficient here because it treats scripts as text content
 * rather than executable code.
 *
 * @param html - The HTML snippet to render.
 * @returns A div with the HTML snippet injected into it.
 */
const HtmlSnippetComponent: FC<HtmlSnippetProps> = ({ id, html }) => {
    const elRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!elRef.current || !html) return;
        const range = document.createRange();
        range.selectNode(elRef.current);
        const documentFragment = range.createContextualFragment(html);

        elRef.current.innerHTML = '';
        elRef.current.append(documentFragment);
    }, [html]);

    return html ? (
        <Content
            ref={elRef}
            className={classNames(styles.container, id)}
            serverHtml={html}
            sanitize={false}
            dataTestId={id}
        />
    ) : null;
};

export const HtmlSnippet: FC<HtmlSnippetProps> = (props) =>
    isServer ? (
        <Content
            className={classNames(styles.container, props.id)}
            serverHtml={props.html}
            sanitize={false}
            dataTestId={props.id}
        />
    ) : (
        <HtmlSnippetComponent {...props} />
    );
